import { useRef, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FormControl, FormHelperText, InputLabel, MenuItem, Paper, Select } from '@mui/material';
import { AttachFile } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useSendMessageStore, useThreadsStore, useUsersStore } from '@lib/store';
import { Attachments } from '@widgets';
import { Button, IconButton, Input } from '@shared/widgets';

const useStyles = makeStyles(() => ({
  container: {
    minWidth: '330px',
  },
  caption: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  post: {
    padding: '32px',
  },
  reply: {
    padding: '20px',
  },
  header: {
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: 1.33,
    marginTop: 0,
    marginBottom: '16px',
    textAlign: 'center',
  },
  form: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  controls: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '24px',
  },
  action: {},
  uploadField: {
    opacity: 0,
    position: 'absolute',
    zIndex: -1000,
  },
}));

interface FormData {
  groupId: string;
  receiverId: string;
  subject: string;
  body: string;
  attachments: File[];
}
const DEFAULT_VALUES: FormData = { groupId: '', receiverId: '', subject: '', body: '', attachments: [] };

interface SendMessageProps {
  className?: string;
  mode?: 'post' | 'reply';
  data?: { threadId?: string; receiverId?: string; subject?: string };
  canClose?: boolean;
  onClose?: () => void;
  onSubmit?: () => void;
}

export const SendMessage = ({ mode = 'post', ...props }: SendMessageProps) => {
  const { data: receivers, getUsers } = useUsersStore();
  const { loading } = useThreadsStore();
  const { sendMessage } = useSendMessageStore();

  const refUpload = useRef<HTMLInputElement | null>(null);

  const { control, formState, handleSubmit, watch, setValue, reset } = useForm<FormData>({ defaultValues: DEFAULT_VALUES });
  const attachments = watch('attachments');

  const styles = useStyles();

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    reset(props.data || DEFAULT_VALUES);
  }, [props.data]);

  const submitHandler = async (data: FormData) => {
    await sendMessage(data);
    reset(DEFAULT_VALUES);
    props.onSubmit?.();
  };

  const classMode = mode === 'post' ? styles.post : styles.reply;
  return (
    <Paper className={clsx(styles.container, classMode)}>
      {mode === 'post' && <h3 className={styles.header}>New Message</h3>}
      <form className={styles.form} onSubmit={handleSubmit(submitHandler)}>
        <div className={styles.controls}>
          {mode !== 'post' && props.canClose && (
            <div className={styles.caption}>
              <span />
              <IconButton onClick={props.onClose} size='large'>
                <CloseIcon />
              </IconButton>
            </div>
          )}
          {mode === 'post' && (
            <Controller
              name='receiverId'
              control={control}
              rules={{ required: 'Receiver is required' }}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth margin='normal' error={!!formState.errors.receiverId}>
                  <InputLabel>Receiver *</InputLabel>
                  <Select {...field} variant='standard' name='receiverId' label='Receiver' fullWidth>
                    {receivers
                      ? receivers.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {[item.firstname, item.lastname].filter((item) => !!item).join(' ')}
                          </MenuItem>
                        ))
                      : null}
                  </Select>
                  {error?.message && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          )}
          {mode === 'post' && (
            <Controller
              name='subject'
              control={control}
              rules={{ required: 'Subject is required' }}
              render={({ field, fieldState: { error } }) => (
                <Input {...field} label='Subject' name='subject' required error={!!error} helperText={error?.message} />
              )}
            />
          )}
          <Controller
            name='body'
            control={control}
            rules={{ required: 'Message is required' }}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                label='Enter your message'
                name='message'
                multiline
                autoFocus={mode === 'reply'}
                required
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </div>
        {!!attachments?.length && (
          <Attachments
            mode='upload'
            items={attachments}
            onDelete={(index) =>
              setValue(
                'attachments',
                attachments.filter((_, idx) => idx !== index),
              )
            }
          />
        )}
        <div className={styles.actions}>
          <Controller
            name='attachments'
            control={control}
            render={({ field: { value, onChange } }) => (
              <input
                ref={refUpload}
                className={styles.uploadField}
                name='attachments'
                type='file'
                multiple
                onChange={(e) => onChange([...value, ...Array.from(e.currentTarget?.files || [])])}
              />
            )}
          />
          <Button type='button' startIcon={<AttachFile />} onClick={() => refUpload.current?.click()}>
            Attach
          </Button>
          <Button color='primary' type='submit' loading={loading || formState.isSubmitting}>
            {mode === 'reply' ? 'Reply' : 'Send'}
          </Button>
        </div>
      </form>
    </Paper>
  );
};
